import type { AnyAction, Middleware, MiddlewareAPI } from "redux";
import { isActionOf } from "typesafe-actions";

import type { Signal } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { toSignalTypeKey } from "app/pages/sales-intelligence/domain/signals/utils/signal-key";
import { advancedSearchFilteringQueryClient } from "app/pages/sales-intelligence/modules/advanced-search-filtering/query/client";
import { applicationStateSyncService } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/services/state-sync";
import { setApplicationStateChangeMessage } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/store/effects";
import { companyAnalysisQueryClient } from "app/pages/sales-intelligence/modules/company-analysis/queries/client";
import { leadScoringQueryClient } from "app/pages/sales-intelligence/modules/lead-scoring-agent/query/client";
import { selectFiltersValue } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { advancedSearchQueryClient } from "app/pages/sales-intelligence/pages/advanced-search/query/client";
import { invalidateOpportunityListTablesQueriesThunk } from "app/pages/sales-intelligence/pages/opportunity-list/store/effects";
import * as profileActions from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/store/action-creators";
import { selectProfilesList } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/store/selectors";
import * as signalActions from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import { selectAllSignalsFromSource } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";
import type { RootState, ThunkDispatchCommon } from "single-spa/store/types";

const autoPinFilterForFirstSignalOfType = (allSignals: readonly Signal[], newSignal: Signal): void => {
    const isFirstOfType = allSignals.filter((s) => s.signalType === newSignal.signalType).length === 1;

    if (!isFirstOfType) {
        return;
    }

    void advancedSearchFilteringQueryClient.pinFilter(toSignalTypeKey(newSignal.signalType));
};

/**
 * Sales Intelligence actions middleware that handles side effects for signals and profiles CRUD operations
 * and notifies the AI agent about state changes via system messages.
 *
 * This middleware triggers setApplicationStateChangeMessage after successful
 * create, update, or delete operations on signals or contact profiles,
 * allowing the AI agent to stay synchronized with the application state.
 */
const siActionsMiddleware: Middleware<Record<PropertyKey, unknown>, RootState, ThunkDispatchCommon> =
    (store: MiddlewareAPI<ThunkDispatchCommon, RootState>) =>
    (next: (action: AnyAction) => AnyAction) =>
    (action: AnyAction): AnyAction => {
        const result = next(action);

        const state = store.getState();

        try {
            switch (true) {
                case isActionOf(
                    [
                        signalActions.createSignalAsync.success,
                        signalActions.updateSignalAsync.success,
                        signalActions.deleteSignalsAsync.success,
                    ],
                    action,
                ): {
                    const signals = selectAllSignalsFromSource(state);

                    void store.dispatch(
                        setApplicationStateChangeMessage(
                            applicationStateSyncService.createSignalsListChangePayload(signals),
                        ),
                    );

                    void store.dispatch(invalidateOpportunityListTablesQueriesThunk());

                    if (isActionOf(signalActions.createSignalAsync.success, action)) {
                        autoPinFilterForFirstSignalOfType(signals, action.payload);
                    }

                    if (
                        isActionOf(
                            [signalActions.updateSignalAsync.success, signalActions.deleteSignalsAsync.success],
                            action,
                        )
                    ) {
                        void advancedSearchQueryClient.invalidateLeadsSearchResultsQueries();
                        void leadScoringQueryClient.invalidateAllQueries();
                        void companyAnalysisQueryClient.invalidateCompanyCardQueries();
                    }

                    break;
                }

                case isActionOf(signalActions.createDraftSignalSuccess, action): {
                    const signals = selectAllSignalsFromSource(state);

                    autoPinFilterForFirstSignalOfType(signals, action.payload);

                    break;
                }

                case isActionOf(signalActions.draftSignalCreatedAndApplied, action): {
                    const signals = selectAllSignalsFromSource(state);
                    const filtersState = selectFiltersValue(state);

                    void store.dispatch(
                        setApplicationStateChangeMessage(
                            applicationStateSyncService.createSignalDraftCreateAndApplyPayload(signals, filtersState),
                        ),
                    );

                    break;
                }

                case isActionOf(signalActions.saveDraftSignalSuccess, action): {
                    void store.dispatch(
                        setApplicationStateChangeMessage(
                            applicationStateSyncService.createSignalLifecycleChangePayload(action.payload),
                        ),
                    );

                    const signals = selectAllSignalsFromSource(state);

                    autoPinFilterForFirstSignalOfType(signals, action.payload);

                    break;
                }

                case isActionOf(
                    [
                        profileActions.createProfileAsync.success,
                        profileActions.updateProfileAsync.success,
                        profileActions.deleteProfileAsync.success,
                    ],
                    action,
                ): {
                    const employeeProfiles = selectProfilesList(state);

                    void store.dispatch(
                        setApplicationStateChangeMessage(
                            applicationStateSyncService.createEmployeeProfilesChangePayload(employeeProfiles),
                        ),
                    );

                    break;
                }
            }
        } catch (error) {
            sentryErrorTrackerWithScope({
                errorMessage: "Failed to notify about application state change",
                e: error,
                params: {
                    extra: {
                        action,
                    },
                },
            });
        }

        return result;
    };

export default siActionsMiddleware;
